import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { useAppContext } from "../../../../AppContext";
import { ISelectFieldModel } from "../../../../common/components/forms/inputs/dropdown/models/selectFieldModel";
import { IAssignableEventModel } from "../models/assignableEventModel";
import {
  buildEventPickerOption,
  IEventPickerOption,
} from "../utils/eventPickerOptionUtils";
import {
  bulkAssignInstructorFormDefaultValues,
  bulkAssignInstructorFormSchema,
  IBulkAssignInstructorFormModel,
} from "../models/bulkAssignInstructorFormModel";

interface IUseBulkAssignInstructorDialogProps {
  visible: boolean;
  employees?: ISelectFieldModel[];
  onHide: () => void;
}

export const useBulkAssignInstructorDialog = ({
  visible,
  employees,
  onHide,
}: IUseBulkAssignInstructorDialogProps) => {
  const { selectedStudioId } = useAppContext();

  const form = useForm<IBulkAssignInstructorFormModel>({
    defaultValues: bulkAssignInstructorFormDefaultValues,
    resolver: yupResolver(bulkAssignInstructorFormSchema),
    mode: "onChange",
  });

  const selectedEmployeeId = form.watch("employeeId");
  const selectedEventIds = form.watch("eventIds") ?? [];
  const hasEmployee = !!selectedEmployeeId && selectedEmployeeId > 0;

  useEffect(() => {
    if (!visible) {
      form.reset(bulkAssignInstructorFormDefaultValues);
    }
  }, [visible, form]);

  // Which services show up depends on the instructor, so a selection made for one employee
  // could contain services the next one is not allowed to perform. Drop it on every switch.
  // The ref keeps the first render from dirtying an already-empty field.
  const previousEmployeeIdRef = useRef(selectedEmployeeId);
  useEffect(() => {
    if (previousEmployeeIdRef.current === selectedEmployeeId) {
      return;
    }

    previousEmployeeIdRef.current = selectedEmployeeId;
    form.setValue("eventIds", []);
  }, [selectedEmployeeId, form]);

  const { data: assignableEvents, isLoading: isQueryLoading } = useApiQuery<
    IAssignableEventModel[]
  >({
    queryKey: [
      endpointsMetadata.planner.getAssignableEventsForInstructor,
      selectedStudioId,
      selectedEmployeeId,
    ],
    endpoint: `${endpointsMetadata.planner.getAssignableEventsForInstructor}?employeeId=${selectedEmployeeId}`,
    enabled: visible && hasEmployee,
    globalIsLoading: false,
    disableErrorToast: false,
    retry: false,
  });

  // React Query reports a DISABLED query as loading, which would leave a skeleton sitting in
  // the field forever while no instructor is picked. Only a query that is actually running
  // counts as loading here.
  const isLoadingEvents = hasEmployee && isQueryLoading;

  const eventOptions: IEventPickerOption[] = useMemo(() => {
    if (!assignableEvents) {
      return [];
    }

    return assignableEvents.map((event) => buildEventPickerOption(event));
  }, [assignableEvents]);

  const { mutate, isLoading: isSaving } = useApiMutation();

  const canSave = hasEmployee && selectedEventIds.length > 0;

  const handleSave = () => {
    if (!canSave) {
      return;
    }

    mutate(
      {
        endpoint: endpointsMetadata.planner.bulkAssignInstructor,
        method: "post",
        globalIsLoading: true,
        payload: {
          employeeId: selectedEmployeeId,
          eventIds: selectedEventIds,
        },
        invalidateKeys: [[endpointsMetadata.planner.getAll]],
      },
      {
        onSuccess: () => {
          onHide();
        },
      },
    );
  };

  return {
    form,
    employeeOptions: employees ?? [],
    eventOptions,
    isLoadingEvents,
    hasEmployee,
    selectedEmployeeId,
    canSave,
    isSaving,
    handleSave,
  };
};
